import type { LogEntry, MuteRule, MuteRuleKind } from '@/types';

export function normalizeMessageForMute(message: string): string {
    return message
        .replace(/\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi, '{uuid}')
        .replace(/\b0x[0-9a-f]+\b/gi, '{hex}')
        .replace(/\b[0-9a-f]{16,}\b/gi, '{hex}')
        .replace(/\b\d+\b/g, '{n}')
        .trim();
}

export function doesLogMatchMuteRule(log: LogEntry, rule: MuteRule): boolean {
    switch (rule.kind) {
        case 'message_exact':
            return log.message.trim() === rule.value;
        case 'message_normalized':
            return normalizeMessageForMute(log.message) === rule.value;
        case 'service':
            return (log.service || '').trim() === rule.value;
        default: {
            const _exhaustiveCheck: never = rule.kind;
            return _exhaustiveCheck;
        }
    }
}

export function isLogMuted(log: LogEntry, rules: MuteRule[]): boolean {
    for (const rule of rules) {
        if (doesLogMatchMuteRule(log, rule)) return true;
    }
    return false;
}

export function describeMuteRule(rule: Pick<MuteRule, 'kind' | 'value'>): { label: string; title: string } {
    const truncateValue = (value: string) => (value.length > 80 ? `${value.slice(0, 77)}...` : value);

    switch (rule.kind) {
        case 'message_exact':
            return { label: `Message: ${truncateValue(rule.value)}`, title: `Muted exact message: ${rule.value}` };
        case 'message_normalized':
            return { label: `Similar: ${truncateValue(rule.value)}`, title: `Muted similar messages (normalized): ${rule.value}` };
        case 'service':
            return { label: `Service: ${truncateValue(rule.value)}`, title: `Muted service: ${rule.value}` };
        default: {
            const _exhaustiveCheck: never = rule.kind;
            return _exhaustiveCheck;
        }
    }
}

export function getMuteRuleValueFromLog(kind: MuteRuleKind, log: LogEntry): string | null {
    switch (kind) {
        case 'message_exact':
            return log.message.trim();
        case 'message_normalized':
            return normalizeMessageForMute(log.message);
        case 'service':
            return (log.service || '').trim() || null;
        default: {
            const _exhaustiveCheck: never = kind;
            return _exhaustiveCheck;
        }
    }
}
